import { Children, isValidElement, useState } from "react";
import type { ChangeEvent, OptionHTMLAttributes, ReactElement } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { enGB } from "date-fns/locale";
import type { DropdownProps } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ISO_DATE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

const FIRST_YEAR = 2000;
const YEARS_AHEAD = 5;

function parseIsoDate(value: string): Date | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function formatDisplayDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
}

function ThemedDropdown({ value, onChange, children, caption }: DropdownProps) {
  const options = Children.toArray(children).filter(
    (child): child is ReactElement<OptionHTMLAttributes<HTMLOptionElement>> =>
      isValidElement(child),
  );

  const handleChange = (next: string) => {
    onChange?.({
      target: { value: next },
    } as ChangeEvent<HTMLSelectElement>);
  };

  return (
    <Select value={value != null ? String(value) : undefined} onValueChange={handleChange}>
      <SelectTrigger className="h-7 w-fit gap-1 border-0 bg-transparent px-2 text-sm font-medium focus:ring-0 focus:ring-offset-0">
        <SelectValue>{caption}</SelectValue>
      </SelectTrigger>
      <SelectContent position="popper" className="max-h-60">
        {options.map((option) => (
          <SelectItem key={String(option.props.value)} value={String(option.props.value)}>
            {option.props.children}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function XeroRetrievalDateCell({
  draft,
  tenantName,
  isPending,
  onDraftChange,
}: {
  draft: string | null;
  tenantName: string;
  isPending: boolean;
  onDraftChange: (next: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = draft ? parseIsoDate(draft) : null;

  const pick = (next: string | null) => {
    setOpen(false);
    onDraftChange(next);
  };

  const thisYear = new Date().getFullYear();
  const selectedYear = selected?.getFullYear();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isPending}
          className="h-8 w-[150px] justify-start gap-2 font-normal"
          aria-label={`Retrieval date for ${tenantName}`}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          {selected ? (
            formatDisplayDate(selected)
          ) : (
            <span className="text-muted-foreground">Set date</span>
          )}
        </Button>
      </PopoverTrigger>
      {/* PopoverContent portals, so this is not clipped by the table. It also
          unmounts when closed, so defaultMonth applies on every open: the
          calendar always starts on the drafted month, else the current one. */}
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          required
          selected={selected ?? undefined}
          onSelect={(day) => day && pick(toIsoDate(day))}
          defaultMonth={selected ?? new Date()}
          locale={enGB}
          captionLayout="dropdown-buttons"
          fromYear={Math.min(FIRST_YEAR, selectedYear ?? FIRST_YEAR)}
          toYear={Math.max(thisYear + YEARS_AHEAD, selectedYear ?? 0)}
          components={{
            Dropdown: ThemedDropdown,
            IconLeft: () => <ChevronLeft className="h-4 w-4" />,
            IconRight: () => <ChevronRight className="h-4 w-4" />,
          }}
          classNames={{
            caption_dropdowns: "flex items-center justify-center gap-1",
            caption_label: "hidden",
            vhidden: "sr-only",
          }}
        />

        {draft && (
          <div className="border-t border-border p-2">
            {/* A selected day cannot be deselected by clicking it again, so
                clearing is an explicit affordance. */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => pick(null)}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
